'use client';

import React from 'react';
import { type FragmentType, getFragmentData } from '@packages/gql/generated/shopping';
import { Flag } from '@packages/shared/src/ui/Flag/Flag';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { Link } from '@packages/modules/src/Link/Link';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { WishlistLineItemsFragmentFragmentDoc } from '@packages/gql/generated/shopping/WishlistLineItemsFragmentFragmentDoc';
import { buildGlycerinWishlistProduct } from '@packages/modules/src/helpers/buildGlycerinObject/buildGlycerinObject';
import { EfficiencyFlags } from '@packages/modules/src/EfficiencyFlags/Order/EfficiencyFlags.client';
import { LineItemPrice } from './LineItemPrice.client';
import { addToBasketStyleOverridesGalleryViewTailwind } from '../Actions/Actions.styles';
import { BasketAction } from '../Actions/AddToBasketAction';
import { SimilarProductAction } from '../Actions/SimilarProductsAction';
import { DeleteAction } from '../Actions/DeleteAction';

interface WishlistItemsProps {
  data: FragmentType<typeof WishlistLineItemsFragmentFragmentDoc>;
}

export const GalleryViewItem: React.FC<WishlistItemsProps> = ({ data }) => {
  const fragmentData = getFragmentData(WishlistLineItemsFragmentFragmentDoc, data);
  const { language, localizeUrl } = useI18n();

  if (!fragmentData) return null;
  const {
    imageUrl,
    name,
    price,
    sku,
    akl,
    isSoldOut,
    powerEfficiency,
    tireEfficiency,
    extra,
    availability,
  } = fragmentData;
  const glycerinProduct = buildGlycerinWishlistProduct(fragmentData);
  const productLinkParams = new URLSearchParams({ sku }).toString();

  return (
    <div className="mt-0 mb-1.5 w-full break-inside-avoid md:mb-2">
      <div className="shadow-color-grey-dark bg-color-background-base relative flex w-full flex-col gap-1 overflow-visible rounded-[3px] p-1 pb-2 shadow-xs">
        <Link
          href={localizeUrl(`/p/${akl}?${productLinkParams}`, language)}
          className="relative h-[170px] w-full"
        >
          <Image priority unoptimized alt={name} src={imageUrl} fill className="object-contain" />
        </Link>
        <div className="flex flex-col gap-0.5">
          <h6 className="text-size-body-xs">{extra?.brand}</h6>
          <h5 className="text-size-body-base md:text-size-headline-base max-h-[60px] overflow-hidden font-semibold text-ellipsis">
            {name}
          </h5>
          {(powerEfficiency || tireEfficiency) && (
            <div>
              <EfficiencyFlags
                powerFlags={powerEfficiency}
                tireFlags={tireEfficiency}
                tireDataSheet={tireEfficiency?.[0]?.dataSheet}
              />
            </div>
          )}
          {!isSoldOut && (
            <div className="md:pt-0">
              <LineItemPrice price={price} alignment="left" />
            </div>
          )}
          <BasketAction
            data={fragmentData}
            galleryView
            className={addToBasketStyleOverridesGalleryViewTailwind}
            glycerinProduct={glycerinProduct}
          />
          {isSoldOut && <SimilarProductAction data={fragmentData} />}
        </div>
        <div className="absolute top-0 right-0 h-auto rounded-lg p-1">
          <DeleteAction
            mode="wishlist"
            lineItemSku={sku}
            overrideButtonClassName="bg-color-background-base hover:bg-color-background-base shadow-md rounded-full border-none w-auto h-auto md:p-0 p-0 md:w-auto md:h-auto"
            galleryView
            glycerinProduct={glycerinProduct}
          />
        </div>
        {availability.state === 'NOT_AVAILABLE' && (
          <div className="absolute top-0 left-0 h-auto p-1">
            <Flag
              text={
                availability.message.charAt(0).toUpperCase() + availability.message.slice(1) // To make first letter uppercase.
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};
